import React from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { BootstrapConfig } from 'core/type/BootstrapConfig.types';
import { PageTypes } from 'core/constant/analytics/PageTypes';
import { getPageType } from 'core/messaging/analytics/pages/pageTypeUtils';
import { setLocation, setPageType } from 'store/slice/navigation';
import { ActivitiesHeaderEntry } from 'component/common/Header/ActivitiesHeaderEntry';
import { getHeader, getWebAnalytics, setWebAnalytics } from 'core/window/windowService';
import { ConsentBannerComponent } from 'component/common/ConsentBanner/ConsentBanner';
import { configurePerformanceClient, measureNavigation, measurePerformancePageLandingAsync } from 'core/Performance/PerformanceService';
import { notifyRiskified } from 'core/service/measurements/riskifiedHelper';
import { setupWebAnalytics } from 'core/messaging/analytics/analytics';
import { setupAnalyticsContext } from 'core/messaging/analytics/activitiesAnalyticsContext';
import GenericErrorOverlay from 'component/common/GenericError/GenericError';
import getActivitiesAppContext from 'core/messaging/analytics/ActivitiesAppDataContext';
import { CommonOverlays } from 'component/common/CommonOverlays/CommonOverlays';
import { buildMeasurement } from 'core/service/measurements/measurementUtils';
import { ActivitiesMessagingClient } from 'core/messaging/ActivitiesMessagingClient';
import { MeasurementName } from 'core/constant/MeasurementName';
import SeoHelmet from 'component/common/SEOHelmet/SEOHelmet';

interface ActivitiesPageWrapperProps {
    config: BootstrapConfig;
    page: React.ReactNode;
    pageType: PageTypes;
}

const getPageTransitionClassName = (isRtl: boolean) => {
    return isRtl ? 'PageTransition PageTransition__rightMenu' : 'PageTransition PageTransition__leftMenu';
};

export const measurementPageVisit = (pageTypeId: number) => {
    const measurement = buildMeasurement('pageVisit', MeasurementName.PageVisit, pageTypeId);
    const measurementMessage = ActivitiesMessagingClient.create('measurement', measurement);
    ActivitiesMessagingClient.send(measurementMessage);
};

export const ActivitiesPageWrapper: React.FC<ActivitiesPageWrapperProps> = React.memo((props) => {
    const location = useLocation();
    const dispatch = useDispatch();
    const pageTypeId = getPageType(props.config.browser, props.pageType);

    React.useEffect(() => {
        // Notify Riskified at the highest priority
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        notifyRiskified(location.pathname, pageTypeId);
    }, [location.pathname, pageTypeId]);

    React.useEffect(() => {
        dispatch(setLocation({ location }));
        measurePerformancePageLandingAsync();
    }, [dispatch, location]);

    React.useLayoutEffect(() => {
        dispatch(setPageType({ pageTypeId }));

        // Measure page performance, do not change order of these calls
        configurePerformanceClient(props.config, pageTypeId);
        measureNavigation(pageTypeId);
        measurementPageVisit(pageTypeId);

        // Must be called with-in a Provider context
        const activitiesAppContext = getActivitiesAppContext(props.config);
        setWebAnalytics(setupWebAnalytics(activitiesAppContext, props.config));
        if (getWebAnalytics()) {
            setupAnalyticsContext(pageTypeId, props.config);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageTypeId]);

    return (
        <>
            <SeoHelmet pageTypeId={pageTypeId} />
            <ActivitiesHeaderEntry header={getHeader()} />
            <GenericErrorOverlay />
            <div className={getPageTransitionClassName(props.config.language.isRtl)} data-element-name="page-transition">
                <React.Suspense fallback={null}>{props.page}</React.Suspense>
            </div>
            <ConsentBannerComponent />
            <CommonOverlays />
        </>
    );
});
ActivitiesPageWrapper.displayName = 'ActivitiesPageContent';
